<template>
  <div
    class="product-order-area"
    :class="{'product-order-area--raid-events': raidEvents}">
    <transition name="transition--opacity">
      <div
        v-if="showSalesEnd"
        class="product-order-area__timer product-order-area__block">
        <div class="product-order-area__timer-title">
          <span>Offer expires in</span>

          <span>Hurry up 💪</span>
        </div>

        <time-counter
          :end-time="product.meta.saleEndTime"
          class="product-order-area__timer-countdown"
          @timerStop="handleSaleTimerStop" />
      </div>

      <div
        v-if="hasRaidEvents"
        class="product-order-area__raid-slots">
        <raid-event-tabs
          :raid-events="raidEvents"
          @select-time-slot="handleSelectTimeSlot" />
      </div>
    </transition>

    <div>
      <product-options
        ref="productOptions"
        class="product-order-area__block"
        :product="product"
        :applied-cashback="usedCashback"
        :runs-count="runsCount"
        :all-product-options="allProductOptions"
        @options="allProductOptions = $event"
        @difficulty="selectedDifficulty = $event"
        @available-raid-events="handleAvailableRaidEvents"
        @execution-time="handleTotalExecutionTimeUpdate"
        @price="calcPrice">
        <product-promo-block
          v-if="promoBlockText"
          class="product-order-area__promo-block">
          {{ promoBlockText }}
        </product-promo-block>
      </product-options>
    </div>

    <order-area
      class="product-order-area__block"
      :order-error="orderError"
      :loading="loading"
      :error="error"
      :price="price.newPrice"
      :old-price="price.oldPrice"
      :product="product"
      :available-stripe-payment-methods="availableStripePaymentMethods"
      :is-available="isAvailable"
      :is-payment-method-opened="paymentMethodSelectorOpened"
      @promocode-applied="applyPromocode"
      @buy-with-stripe="buyWithStripe"
      @open-whats-next="openWhatsNextPopup"
      @open-payment-method-selector="openPaymentMethodSelector"
      @cashback-applied="usedCashback = $event"
      @runs-count="runsCount = $event"
      @discount-by-quantity="changeDiscountByQuantity" />
  </div>
</template>

<script>
import { canSupportApplePay, scrollTo, addBuyLogs } from 'Root/helpers/tools';
import { urlRegexp, regExpSelfplay } from 'Root/helpers/regexps';
import ProductOptions from 'Components/OrderArea/ProductOptions.vue';
import OrderArea from 'Components/OrderArea/OrderArea.vue';
import PaymentMethodSelector from 'Components/PaymentPopups/PaymentMethodSelector.vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { getAdvertisingParamsFromSession } from 'Root/helpers/handleAdvertisingParams';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import altcraftAnalytics from 'Root/drivers/altcraftAnalytics';
import criteoAnalytics from 'Root/drivers/criteoAnalytics';
import AppModel from 'Models/AppModel';
import { POPUP_PROMOCODE_APPLIED } from 'Models/PopupModel';
import ProductPromoBlock from 'Root/pages/game/components/ProductPromoBlock.vue';
import FactionModel from 'Models/FactionModel';
import PaymentSystemModel from 'Models/PaymentSystemModel';
import PromocodeModel from 'Models/PromocodeModel';
import RegionModel from 'Models/RegionModel';
import GameModel from 'Models/GameModel';
import WhatsNextPopup from 'Components/Popups/WhatsNextPopup.vue';
import TimeCounter from 'Components/ui/TimeCounter.vue';
import fingerprintHeader from 'Root/mixins/fingerprintHeader';
import { getPriceWithCommission } from 'Root/helpers/product';
import UserModel from 'Models/UserModel';
import tiktokTagAnalytics from 'Root/drivers/tiktokTagAnalytics';
import RaidEventTabs from './RaidEventTabs.vue';

const DEFAULT_ERROR_MESSAGE = 'An error has occurred. Try again.';
const { API_URI } = AppModel;

export default {
  name: 'ProductOrderArea',

  components: {
    ProductPromoBlock,
    ProductOptions,
    OrderArea,
    TimeCounter,
    RaidEventTabs,
  },

  mixins: [fingerprintHeader],

  props: {
    product: {
      type: Object,
      required: true,
    },

    displayPrice: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      selectedFactionId: FactionModel.ALLIANCE.id,
      selectedPaymentMethod: null,
      price: {
        newPrice: null,
        oldPrice: null,
        purePrice: null,
        initialPrice: null,
        initialOldPrice: null,
      },
      appliedPromocode: null,
      discountByQuantity: null,
      loading: false,
      error: '',
      orderError: '',
      FactionModel,
      wasMounted: false,
      availableStripePaymentMethods: {
        googlePay: false,
      },
      allProductOptions: [],
      isGuest: false,
      stripePaymentRequest: false,
      stripe: false,
      selectedDifficulty: null,
      usedCashback: {
        value: 0,
        isApplied: false,
      },
      currTime: Date.now(),
      FINGERPRINT_isSilent: true,
      runsCount: 1,
      paymentMethodSelectorOpened: false,
      userEmail: null,
      paypalPrice: null,
      raidEvents: [],
      selectedRaidTimeSlot: null,
    };
  },

  computed: {
    ...mapGetters(['currencyValue', 'isUsersFirstOrder']),
    ...mapState(['user', 'activeRegion', 'userDevice', 'countryCode', 'usdToCurrencyRatio']),
    ...mapState('products', ['tags']),

    isAvailable() {
      return this.product.available_in_regions.includes(this.activeRegion.id);
    },

    isGoldProduct() {
      return this.product.is_currency_offer_finance_report;
    },

    selfplayOption() {
      const optionsAndDifficulties = [...this.allProductOptions];

      if (this.selectedDifficulty) {
        optionsAndDifficulties.push({
          ...this.selectedDifficulty,
          isChecked: true,
        });
      }

      /* Проверяем не только то, что селфплей находится в списке, но и ставим чекнутый на первую позицию */
      return optionsAndDifficulties
        .filter((o) => (regExpSelfplay).test(o.title) && !o.disabled)
        .sort((a, b) => {
          if (a.isChecked && !b.isChecked) {
            return -1;
          }
          if (!a.isChecked && b.isChecked) {
            return 1;
          }
          return 0;
        })[0];
    },

    isCoaching() {
      return this.product.tags.some((tag) => (/coaching/i).test(tag.title));
    },

    promoBlockText() {
      return this.product.banners?.promo;
    },

    isSaleActive() {
      if (!this.product.meta?.saleEndTime) {
        const region = this.activeRegion.shortTitle.toLowerCase();
        return +this.product[`cost_${region}_old`] > +this.product[`cost_${region}`];
      }

      const timezoneOffsetMS = new Date().getTimezoneOffset() * 60 * 1000;
      const endTimeSec = Date.parse(new Date(this.product.meta.saleEndTime)) / 1000;
      const currTimeSec = (this.currTime + timezoneOffsetMS) / 1000;
      return endTimeSec - currTimeSec >= 1;
    },

    showSalesEnd() {
      return this.product.meta.saleEndTime && this.isSaleActive;
    },

    hasRaidEvents() {
      return this.raidEvents.length;
    },
  },

  watch: {
    'price.newPrice': {
      handler(newVal) {
        this.$emit('update:display-price', newVal);
      },
    },

    selectedPaymentMethod(val) {
      this.price.newPrice = this.price.purePrice;
      this.calcPrice(this.price);
      if (val === PaymentSystemModel.METHODS.PAYPAL.id || val === PaymentSystemModel.METHODS.EPOCH.id) {
        this.paypalPrice = this.price.newPrice;
      }
    },
  },

  mounted() {
    this.selectedFactionId = this.$route.params.faction || FactionModel.ALLIANCE.id;
    this.wasMounted = true;

    const game = GameModel.ID_TO_DATA[this.product.game].fullTitle;
    const currency = RegionModel.ID_TO_DATA[this.$store.state.activeRegion.id].currency.title;
    googleTagAnalytics.emitProductDetailNew(game, this.product, this.price.newPrice, currency);
    const productTag = this.tags.find((tag) => tag.id === this.product.master_tag_id);

    googleTagAnalytics.firePageView(this.product, productTag, this.price.newPrice);
    tiktokTagAnalytics.fireViewContent(this.user);

    if (this.isAvailable) {
      if (window.Stripe) {
        this.checkStripeAvailableMethods();
      } else {
        const stripeScript = document.getElementById('stripe-script');
        if (!stripeScript) {
          return;
        }

        stripeScript.onload = () => {
          this.checkStripeAvailableMethods();
        };
      }
    }
  },

  methods: {
    ...mapActions(['getUser']),

    handleSelectTimeSlot(slot) {
      this.selectedRaidTimeSlot = slot;
    },

    parsePriceToStripeAmount(price) {
      let amount = 0;

      if (String(price).includes('.')) {
        const splittedPrice = String(price).split('.');
        amount = (Number(splittedPrice[0]) * 100) + Number(splittedPrice[1]);
      } else {
        amount = price * 100;
      }

      return +amount;
    },

    async checkStripeAvailableMethods() {
      this.stripe = window.Stripe(PaymentSystemModel.STRIPE_PUBLIC_KEY);

      const country = this.countryCode;
      const currency = this.activeRegion.currency.title.toLowerCase();

      if (!country) {
        return;
      }

      this.stripePaymentRequest = this.stripe.paymentRequest({
        country,
        currency,
        /**
         * {@link https://stripe.com/docs/js/appendix/payment_item_object}
         */
        total: {
          label: this.product?.title,
          amount: this.parsePriceToStripeAmount(this.price.newPrice.toFixed(2)),
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      let canMakePaymentVia = null;

      try {
        canMakePaymentVia = await this.stripePaymentRequest.canMakePayment();
        // eslint-disable-next-line no-empty
      } catch (e) {}

      if (canMakePaymentVia) {
        if (Object.keys(canMakePaymentVia).includes('googlePay') && !canSupportApplePay()) {
          this.availableStripePaymentMethods.googlePay = true;
          this.activePaymentSystem = PaymentSystemModel.METHODS.GOOGLE_PAY.id;
        }
      }
    },

    validateOptions() {
      let isValid = true;

      if (!this.$refs.productOptions.isValid) {
        this.$refs.productOptions.isLoadMoreClicked = true;
        this.$refs.productOptions.showRequiredAlert = true;
        isValid = false;
      }

      return isValid;
    },

    openWhatsNextPopup() {
      this.$vuedals.openMobilePanel({
        class: 'whats-next-popup',
        title: 'What\'s next?',
        component: WhatsNextPopup,
        closeOnBackdrop: true,
      });
    },

    openPaymentMethodSelector(userEmail) {
      this.userEmail = userEmail;

      if (!this.validateOptions()) {
        if (this.userDevice.innerWidth < 720) {
          const { top } = this.$refs.productOptions.$el.getBoundingClientRect();
          // 47 - высота тултипа + отступ 10px
          const y = top + window.pageYOffset - 47;
          scrollTo(0, y);
        }

        googleTagAnalytics.fireError('buy_now_1', 'chose_options');

        return;
      }

      this.paymentMethodSelectorOpened = true;

      this.$vuedals.openMobilePanel({
        class: this.selfplayOption && !this.selfplayOption.isChecked ? 'vuedals__payment-selector' : null,
        component: PaymentMethodSelector,
        closeOnBackdrop: true,
        data: {
          component: this,
        },
        onClose: ({ methodId, eWallet }) => {
          this.order((this.user?.email || this.userEmail), methodId, eWallet);
        },
        onDismiss: () => {
          this.paymentMethodSelectorOpened = false;

          this.selectedPaymentMethod = null;
        },
      });

      this.sendGooglePaymentAnalytics();
    },

    sendGooglePaymentAnalytics() {
      const game = GameModel.ID_TO_DATA[this.product.game].fullTitle;
      const currency = RegionModel.ID_TO_DATA[this.$store.state.activeRegion.id].currency.title;
      const runs = this.runsCount;
      googleTagAnalytics.emitOpenPaymentNew(game, this.product, this.price.newPrice, currency, runs);

      const tagId = this.product?.master_tag_id || this.product.tags[0].id;
      const orderTag = this.product.tags.find((tag) => tag.id === tagId);
      googleTagAnalytics.firePaymentPopup(this.product, orderTag.title, this.price.newPrice);

      criteoAnalytics.fireAddToCartEvent(
        this.user?.email || this.userEmail,
        this.product?.id,
        this.price?.newPrice,
        runs,
      );
    },

    disableInterface() {
      this.loading = true;
      this.$store.dispatch('showScreenHolder', 'payment');
    },

    enableInterface() {
      this.loading = false;
      this.$store.dispatch('hideScreenHolder');
    },

    order(email, paymentMethodId, eWallet, shouldRedirect) {
      if (this.loading) {
        return;
      }

      this.error = '';
      this.disableInterface();

      try {
        // eslint-disable-next-line consistent-return
        return this.createOrder(paymentMethodId, eWallet, shouldRedirect, email);
      } catch (e) {
        this.enableInterface();
        googleTagAnalytics.emitBuyItem(false);
        this.error = DEFAULT_ERROR_MESSAGE;
        console.warn(e.message);
      }
    },

    createOrderData(paymentMethodId, eWallet, email) {
      const optionsSelector = this.$refs.productOptions;

      const options = optionsSelector.options.filter((o) => o.isChecked).map((o) => o.id);
      const difficultyOptions = optionsSelector.difficultyOptions.filter((o) => o.isChecked).map((o) => o.id);

      const orderData = {
        runs: this.runsCount,
        options,
        payment_system: paymentMethodId,
        region_id: this.activeRegion.id,
        promocode: this.appliedPromocode?.code || null,
        ...(this.$route.query.upsale_ref ? { upsale_ref: this.$route.query.upsale_ref } : {}),
      };

      if (!this.user) {
        orderData.email = email;
      }

      const meta = {};

      meta.connectionInfo = this.$store.state.connectionInfo;
      meta.advertisingParams = getAdvertisingParamsFromSession();

      if (optionsSelector.rangeValues.range) {
        meta.range = optionsSelector.rangeValues.range;
      }

      if (optionsSelector.productRank) {
        meta.rank = optionsSelector.rangeValues.rank;
      }

      const ewalletMethods = [
        PaymentSystemModel.CARDPAY_2_WEBMONEY.id,
        PaymentSystemModel.CARDPAY_2_QIWI.id,
      ];

      if (ewalletMethods.includes(paymentMethodId)) {
        meta.ewallet_account = eWallet;
      }

      if (optionsSelector.difficultyId !== undefined) {
        meta.difficulty = {
          id: optionsSelector.difficultyId,
          options: difficultyOptions,
        };
      }

      if (this.usedCashback?.isApplied && this.usedCashback?.value) {
        meta.cashback = this.usedCashback.value;
      }

      if (this.selectedRaidTimeSlot) {
        orderData.raid_id = this.selectedRaidTimeSlot.raidId;
        orderData.time = this.selectedRaidTimeSlot.dateTime;
      }

      orderData.meta = JSON.stringify(meta);

      orderData.is_guest = this.isGuest;

      const referee = localStorage.getItem('referee');

      if (referee) {
        orderData.referee = referee;
      }

      const initialURLpage = sessionStorage.getItem('initialURLPage');

      if (initialURLpage) {
        orderData.login_link = initialURLpage;
      }

      return orderData;
    },

    sendAltcraftAnalytics(hasPassword) {
      altcraftAnalytics.firePaymentPopup(this.price.newPrice, this.user, this.userEmail, !hasPassword);
    },

    async createOrder(paymentMethodId, eWallet, shouldRedirect = true, email) {
      const orderData = this.createOrderData(paymentMethodId, eWallet, email);

      let data;
      await this.$axios.post(`${API_URI}/product/${this.product.id}/buy`, orderData, this._getFingerprintHeader())
        .then(async (d) => {
          data = d.data;

          if (data.skycoach_token) {
            await this.getUser();
            this.sendAltcraftAnalytics(this?.user?.has_password);
            localStorage.setItem(UserModel.SET_PASSWORD_TOKEN_NAME, data.skycoach_token);
          } else {
            this.sendAltcraftAnalytics(this?.user?.has_password);
          }
        })
        .catch((error) => {
          this.enableInterface();

          if (error?.response?.data?.message) {
            this.orderError = error?.response?.data?.message;
          } else {
            console.log(error);
          }

          if (this.orderError === 'Promocode usage exceeded') {
            this.appliedPromocode = null;
          }

          addBuyLogs(error);
        });

      this.$cookie.delete(POPUP_PROMOCODE_APPLIED.cookieName);
      googleTagAnalytics.emitBuyItem(true);

      if (!data?.url) {
        return;
      }

      if ('localStorage' in window) {
        localStorage.setItem('activeGameId', this.$store.state.activeGame.id);
      }

      if (shouldRedirect) {
        if (data.url.match(urlRegexp)) {
          const pathToRedirect = new URL(data.url);
          const emailParam = pathToRedirect.searchParams.get('email');
          if (!this.user && !emailParam && paymentMethodId !== PaymentSystemModel.ECOMMPAY.id) {
            pathToRedirect.searchParams.append('email', email);
          } else if (paymentMethodId === PaymentSystemModel.ECOMMPAY.id) {
            pathToRedirect.searchParams.delete('email');
          }

          document.location.href = pathToRedirect.href;
        } else if (window.stripe) {
          const { error } = await window.stripe.redirectToCheckout({
            sessionId: data.url,
          });

          if (error) {
            this.enableInterface();
            throw new Error(`Payment Error: ${error.message}`);
          }
        } else {
          this.enableInterface();
          throw new Error('window.stripe is undefined');
        }
      }

      // eslint-disable-next-line consistent-return
      return Promise.resolve(data);
    },

    applyPromocode(promocode) {
      this.appliedPromocode = promocode;
      this.price.newPrice = this.price.purePrice;
      this.calcPrice(this.price);
    },

    changeDiscountByQuantity(value) {
      this.$set(this, 'discountByQuantity', value);
      this.price.newPrice = this.price.purePrice;
      this.calcPrice(this.price);
    },

    countPriceWithPromocode(promocode, price) {
      if (promocode.type === PromocodeModel.TitleToIdMap.FIXED_DISCOUNT) {
        price -= Math.min(+promocode.value, Math.round((price / 2) * 100) / 100);
      }
      if (+promocode.type === PromocodeModel.TitleToIdMap.PERCENT_DISCOUNT) {
        price -= Math.round(price * promocode.value) / 100;
      }

      return ((price * 100) / 100).toFixed(2);
    },

    calcPrice(price) {
      const {
        newPrice,
        oldPrice,
        initialPrice,
        initialOldPrice,
      } = price;

      this.price.initialPrice = initialPrice;
      this.price.initialOldPrice = initialOldPrice;
      this.price.purePrice = newPrice;
      this.price.oldPrice = oldPrice;

      let finalPrice = getPriceWithCommission(newPrice, this.selectedPaymentMethod, {
        regionId: this.activeRegion.id,
        isFirstPurchase: this.isUsersFirstOrder,
      });

      if (this.appliedPromocode) {
        if (this.selectedDifficulty?.ignorePromocodes) {
          const difficultyPrice = this.selectedDifficulty.price[this.activeRegion.shortTitle.toUpperCase()];
          finalPrice = +this.countPriceWithPromocode(
            this.appliedPromocode,
            finalPrice - difficultyPrice,
          ) + difficultyPrice;
        } else {
          finalPrice = +this.countPriceWithPromocode(this.appliedPromocode, finalPrice);
        }
      }

      if (this.discountByQuantity) {
        finalPrice = +this.countPriceWithPromocode(this.discountByQuantity, finalPrice);
      }

      this.price.newPrice = finalPrice;
    },

    async buyWithStripe(email) {
      if (!this.validateOptions()) {
        return;
      }

      if (this.raidEvents.length && this.selectedRaidTimeSlot === null) {
        return;
      }

      this.selectedPaymentMethod = PaymentSystemModel.METHODS.GOOGLE_PAY.id;

      this.sendGooglePaymentAnalytics();

      const response = await this.order(email, PaymentSystemModel.METHODS.GOOGLE_PAY.id, null, false);
      if (response) {
        const [redirectUrl, clientSecret] = response.url && response.url.includes('&client_secret=')
          ? response.url.split('&client_secret=') : [response.url, null];
        console.log('order response:', redirectUrl, clientSecret);
        const currency = this.activeRegion.currency.title.toLowerCase();

        this.stripePaymentRequest.update({
          currency,
          total: {
            label: this.product?.title,
            amount: this.parsePriceToStripeAmount(this.price.newPrice.toFixed(2)),
          },
        });

        if (clientSecret && redirectUrl) {
          this.listenStripeRequest(redirectUrl, clientSecret);
          this.stripePaymentRequest.show();
        }
      }
    },

    listenStripeRequest(redirectUrl, clientSecret) {
      this.stripePaymentRequest.on('paymentmethod', (event) => {
        console.log('stripePaymentRequest.on: PAYMENT METHOD', event);
        this.$axios.post(`${API_URI}/log/add`, {
          message: `${this.product.id} | Start`,
        });
        // Confirm the PaymentIntent without handling potential next actions (yet).
        this.stripe.confirmCardPayment(
          clientSecret,
          { payment_method: event.paymentMethod.id },
          { handleActions: false },
        ).then((confirmResult) => {
          if (confirmResult.error) {
            // Report to the browser that the payment failed, prompting it to
            // re-show the payment interface, or show an error message and close
            // the payment interface.
            event.complete('fail');
            console.log('FAIL | PAYMENT EVENT COMPLETED');
            this.$axios.post(`${API_URI}/log/add`, {
              message: `${this.product.id} | Payment failed. Payment method: ${event.paymentMethod.id}`,
            });
            this.enableInterface();
          } else {
            // Report to the browser that the confirmation was successful, prompting
            // it to close the browser payment method collection interface.
            event.complete('success');
            console.log('SUCCESS | PAYMENT EVENT COMPLETED');
            this.$axios.post(`${API_URI}/log/add`, {
              message: `${this.product.id} | Payment event completed.
                PaymentIntent status: ${confirmResult.paymentIntent.status}.
                PaymentIntent next_actions: ${confirmResult.paymentIntent.next_actions}`,
            });
            // Check if the PaymentIntent requires any actions and if so let Stripe.js
            // handle the flow. If using an API version older than "2019-02-11" instead
            // instead check for: `paymentIntent.status === "requires_source_action"`.
            if (confirmResult.paymentIntent.status === 'requires_action') {
              // Let Stripe.js handle the rest of the payment flow.
              this.stripe.confirmCardPayment(clientSecret).then((result) => {
                this.$axios.post(`${API_URI}/log/add`, { message: `${this.product.id} | Confirm card payment` });
                if (result.error) {
                  this.$axios.post(`${API_URI}/log/add`, {
                    message: `${this.product.id} | Confirm card payment failed. Error: ${result.error}`,
                  });
                  alert('Failed to create payment. Please try another payment method.');
                  // The payment failed -- ask your customer for a new payment method.
                } else {
                  this.$axios.post(`${API_URI}/log/add`, {
                    message: `${this.product.id} | Confirm card payment Success`,
                  });
                  document.location.href = redirectUrl;
                  // The payment has succeeded.
                }
              });
            } else {
              this.$axios.post(`${API_URI}/log/add`, {
                message: `${this.product.id} | Confirm card payment Success`,
              });
              document.location.href = redirectUrl;
              // The payment has succeeded.
            }
          }
        });
      });

      this.stripePaymentRequest.on('token', (event) => {
        console.log('stripePaymentRequest.on: TOKEN', event);
      });

      this.stripePaymentRequest.on('source', (event) => {
        console.log('stripePaymentRequest.on: SOURCE', event);
      });

      this.stripePaymentRequest.on('cancel', () => {
        console.log('stripePaymentRequest.on: CANCEL');
        this.enableInterface();
      });
    },

    handleSaleTimerStop() {
      this.currTime = Date.now();
      this.calcPrice(this.price);
    },

    handleTotalExecutionTimeUpdate(totalExecutionTime) {
      this.$emit('execution-time', totalExecutionTime);
    },

    handleAvailableRaidEvents(value) {
      this.raidEvents = value;

      if (!this.raidEvents.length) {
        this.handleSelectTimeSlot(null);
      }
    },
  },
};
</script>

<style lang="scss">
.vuedals__payment-selector {
  .mobile-panel-popup__body {
    padding: 0 16px 32px;

    @media screen and #{$screen-more-or-640} {
      padding: 0 40px 40px;
    }
  }
}
</style>

<style lang="scss" scoped>
  .product-order-area {
    border-radius: 10px;
  }

  .product-order-area__timer {
    grid-column: 1 / -1;
  }

  .product-order-area__timer-title {
    font-size: 15px;
    font-weight: 500;
    display: flex;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .product-order-area__timer-countdown {
    --timer-item-flow: row;
  }

  .product-order-area__block {
    &:not(:last-child) {
      margin-bottom: 16px;
    }

    &:last-child {
      margin-bottom: 24px;
    }
  }

  .product-order-area__pill {
    margin-bottom: 16px;
  }

  .product-order-area__raid-slots {
    grid-column: span 2;
    grid-row: 1;
  }

  @media screen and (max-width: 719px) {
    .product-order-area__raid-slots {
      grid-column: 1;
      margin-bottom: 16px;
    }

    .product-order-area__pill {
      --grid-auto-columns: 1fr;
    }

    .product-order-area__promo-block {
      margin: 0 auto 16px;
    }
  }

  @media screen and (min-width: 720px) {
    .product-order-area {
      display: grid;
      grid-row-gap: 30px;
      grid-column-gap: 36px;
      grid-template-columns: 1fr 1fr;

      &--raid-events {
        grid-row-gap: 24px;
      }
    }

    .product-order-area__timer-title {
      font-size: 19px;
    }

    .product-order-area__block:last-child,
    .product-order-area__block:not(:last-child) {
      margin-bottom: 0;
    }

    .product-order-area__promo-block {
      margin-bottom: 16px;
    }
  }

  @media screen and #{$screen-more-or-768} {
    .product-order-area__timer-countdown {
      --timer-number-font-size: 19px;
      --timer-item-flow: column;
      --timer-item-gap: 8px;
    }
  }
</style>
